import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";

type ToastTone = "success" | "error" | "info";
interface Toast { id: number; message: string; tone: ToastTone; }
interface ToastContextValue { notify: (message: string, tone?: ToastTone) => void; }
const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((items) => items.filter((item) => item.id !== id)), []);
  const notify = useCallback((message: string, tone: ToastTone = "success") => {
    const id = nextId.current++;
    setToasts((items) => [...items.slice(-2), { id, message, tone }]);
    if (tone !== "error") window.setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);
  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-[80] flex w-[min(24rem,calc(100%-2rem))] flex-col gap-2 lg:bottom-4 lg:top-auto" aria-live="polite" aria-label="通知">
        {toasts.map((toast) => {
          const Icon = toast.tone === "success" ? CheckCircle2 : toast.tone === "error" ? CircleAlert : Info;
          return (
            <div key={toast.id} className="card flex items-start gap-3 p-3 shadow-[var(--shadow-raised)]" role={toast.tone === "error" ? "alert" : "status"}>
              <Icon className={toast.tone === "success" ? "text-success" : toast.tone === "error" ? "text-danger" : "text-[var(--color-info)]"} size={20} aria-hidden="true" />
              <p className="min-w-0 flex-1 text-sm">{toast.message}</p>
              <button className="icon-btn -m-2" aria-label="关闭通知" onClick={() => dismiss(toast.id)}><X size={18} /></button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used within ToastProvider");
  return value;
}
